import { countriesWithCode, countriesWithFlag } from "@aurem/nationalities";
import { Button, CopyIconButton, Select, SelectOption, Text } from "@aurem/ui";
import { Sparkles } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { DevToolbarCard } from "../components/DevToolbarCard";
import { generateIban, IBAN_COUNTRIES } from "../utils/iban-generator";

const MOHRE_EIDA_LIST = [
  {
    eida: "784194512345683",
    name: "Zainab Ali Al Hashimi",
    company: "Global Innovation Technologies",
  },
  {
    eida: "784198512345678",
    name: "Ahmed Mohamed Al Mansouri",
    company: "Emirates Tech Solutions",
  },
  {
    eida: "784199012345679",
    name: "Sara Khalid Al Zaabi",
    company: "Dubai Digital Services",
  },
  {
    eida: "784197812345680",
    name: "Omar Hassan Al Nuaimi",
    company: "Abu Dhabi Investment Group",
  },
  {
    eida: "784196712345681",
    name: "Fatima Abdullah Al Qasimi",
    company: "Sharjah Manufacturing",
  },
  {
    eida: "784195612345682",
    name: "Khalid Mohammed Al Nuaimi",
    company: "Ajman Construction",
  },
  {
    eida: "784198416149577",
    name: "Muhammad Yasir Farooqi",
    company: "Al Jaber Transport (Jaber)",
  },
  {
    eida: "784199587471907",
    name: "Ruwayya Alhameidy",
    company: "The White Tea Shop",
  },
];

const DEFAULT_COUNTRY_CODE =
  process.env.DEPLOYMENT_COUNTRY?.toUpperCase() || "AE";

const IbanGeneratorCard = () => {
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [generatedIban, setGeneratedIban] = useState<string | null>(null);

  const countryOptions: SelectOption[] = useMemo(() => {
    return IBAN_COUNTRIES.map((code) => {
      const flag = countriesWithFlag.find((c) => c.code === code)?.flag || "";
      const name = countriesWithCode[code] || code;
      return {
        id: code,
        text: name,
        icon: flag,
      };
    }).sort((a, b) => a.text.localeCompare(b.text));
  }, []);

  const handleGenerate = useCallback(() => {
    const iban = generateIban(countryCode);
    setGeneratedIban(iban);
  }, [countryCode]);

  const handleCountryChange = useCallback((value: string | undefined) => {
    if (value) {
      setCountryCode(value);
      setGeneratedIban(null);
    }
  }, []);

  return (
    <DevToolbarCard title="IBAN Generator" testId="devtoolbar-iban-generator">
      <div className="flex flex-col gap-2">
        <Select
          options={countryOptions}
          value={countryCode}
          onChange={handleCountryChange}
          size="sm"
          data-e2e-id="devtoolbar-iban-country-select"
        />

        {generatedIban && (
          <div className="flex items-center gap-2 p-2 bg-white border border-gray200 rounded">
            <Text size="xs" className="flex-1 font-mono break-all">
              {generatedIban}
            </Text>
            <CopyIconButton
              copyContent={generatedIban}
              variant="ghost"
              color="neutral"
              size="xs"
              data-e2e-id="devtoolbar-iban-copy"
            />
          </div>
        )}

        <div className="flex justify-end">
          <Button
            size="xs"
            onClick={handleGenerate}
            startIcon={<Sparkles size={12} />}
            data-e2e-id="devtoolbar-iban-generate"
          >
            Generate
          </Button>
        </div>
      </div>
    </DevToolbarCard>
  );
};

const MOHRE_OTP_CODE = "123456";

const MohreEidaCard = () => (
  <DevToolbarCard title="MOHRE" testId="devtoolbar-mohre-eida">
    <div className="flex flex-col gap-1 max-h-[148px] overflow-y-auto">
      <div className="flex items-center gap-2 p-1.5 bg-primary50 border border-primary200 rounded">
        <div className="flex-1">
          <Text size="xs" className="font-mono font-medium">
            OTP: {MOHRE_OTP_CODE}
          </Text>
        </div>
        <CopyIconButton
          copyContent={MOHRE_OTP_CODE}
          variant="ghost"
          color="neutral"
          size="xs"
          data-e2e-id="devtoolbar-mohre-copy-otp"
        />
      </div>
      {MOHRE_EIDA_LIST.map((item) => (
        <div
          key={item.eida}
          className="flex items-center gap-2 p-1.5 bg-white border border-gray200 rounded hover:bg-gray50"
        >
          <div className="flex-1">
            <Text size="xs">{item.eida}</Text>
            <Text size="xs" className="text-gray400">
              {item.company}
            </Text>
          </div>
          <CopyIconButton
            copyContent={item.eida}
            variant="ghost"
            color="neutral"
            size="xs"
            data-e2e-id={`devtoolbar-mohre-copy-${item.eida}`}
          />
        </div>
      ))}
    </div>
  </DevToolbarCard>
);

export const DataTab = () => (
  <div
    className="px-4 py-3 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3"
    data-e2e-id="devtoolbar-data-tab"
  >
    <MohreEidaCard />
    <IbanGeneratorCard />
  </div>
);
